<script lang="ts">
import InlineEmojiPickerPopup from '@/components/InlineEmojiPickerPopup.svelte'
import { type EmojiItem, emojiStore, getEmojiColonMatch, replaceEmojiColon, searchEmojis } from '@/modules/emojis'
import { Key } from '@/modules/textarea'

type Props = {
	textareaRef: HTMLTextAreaElement | null
	inputValue: string
	isOpen?: boolean
	listboxId?: string
	activeDescendantId?: string | undefined
	onEmojiInsert: (newText: string, newCursorPosition: number) => void
}

const emojiPickerListboxId = `emoji-picker-${Date.now().toString(36)}`

let {
	textareaRef,
	inputValue,
	isOpen = $bindable(false),
	listboxId = $bindable(emojiPickerListboxId),
	activeDescendantId = $bindable(undefined),
	onEmojiInsert,
}: Props = $props()

let matchingEmojis: EmojiItem[] = $state([])
let selectedIndex = $state(0)

// Update bindable props
$effect(() => {
	isOpen = matchingEmojis.length > 0
})

$effect(() => {
	activeDescendantId = matchingEmojis.length > 0 ? `${emojiPickerListboxId}-option-${selectedIndex}` : undefined
})

// Update emoji suggestions based on current input and cursor position
const updateSuggestions = async () => {
	if (!textareaRef) {
		matchingEmojis = []
		return
	}

	const cursorPosition = textareaRef.selectionStart ?? 0
	const query = getEmojiColonMatch(inputValue, cursorPosition)

	if (query) {
		const allEmojis = await emojiStore.loadData()

		matchingEmojis = searchEmojis(query, allEmojis)
		if (selectedIndex >= matchingEmojis.length) {
			selectedIndex = 0
		}
	} else {
		matchingEmojis = []
		selectedIndex = 0
	}
}

const selectEmoji = (emoji: EmojiItem) => {
	if (!textareaRef) return

	const cursorPosition = textareaRef.selectionStart ?? 0
	const { newText, newCursorPosition } = replaceEmojiColon(inputValue, cursorPosition, emoji.native)

	matchingEmojis = []
	selectedIndex = 0

	onEmojiInsert(newText, newCursorPosition)
}

export const close = () => {
	matchingEmojis = []
	selectedIndex = 0
}

/**
 * Handle keyboard events for emoji picker navigation.
 * Returns true if the event was handled (should prevent default).
 */
export const handleKeyDown = (event: KeyboardEvent): boolean => {
	if (!isOpen) return false

	const { key } = event

	if (key === Key.ArrowDown) {
		selectedIndex = (selectedIndex + 1) % matchingEmojis.length
		return true
	}

	if (key === Key.ArrowUp) {
		selectedIndex = selectedIndex === 0 ? matchingEmojis.length - 1 : selectedIndex - 1
		return true
	}

	if (key === Key.Enter || key === Key.Tab) {
		if (matchingEmojis[selectedIndex]) {
			selectEmoji(matchingEmojis[selectedIndex])
		}
		return true
	}

	if (key === Key.Escape) {
		close()
		return true
	}

	return false
}

/**
 * Call this when input changes or cursor position changes.
 */
export const handleInputChange = () => {
	void updateSuggestions()
}

/**
 * Call this when cursor position changes (click, arrow keys).
 */
export const handleSelectionChange = () => {
	if (!isOpen) return
	void updateSuggestions()
}
</script>

{#if isOpen}
	<InlineEmojiPickerPopup
		emojis={matchingEmojis}
		{selectedIndex}
		listboxId={emojiPickerListboxId}
		onEmojiSelect={selectEmoji}
		onClose={close}
	/>
{/if}
